import React, { memo, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FavoriteButton from '../FavoriteButton';
import ListingCardImage from './ListingCardImage';
import ListingCardPrice from './ListingCardPrice';
import ListingCardBadges, { FeaturedIcon } from './ListingCardBadges';
import ListingCardHeader, { ListingCardFooter } from './ListingCardHeader';
import { useIsland } from '../../App';
import { MapPin, Eye, Gavel, Package, Wallet, Zap } from 'lucide-react';
import { useCurrency, useLanguage } from '../../App';
import { getRentalPeriodLabel } from '../../utils/currencyFormatter';

/**
 * ListingCard
 * 
 * Modular, performance-optimized listing card component.
 * Uses React.memo and component composition for efficient re-renders.
 * 
 * Features:
 * - Lazy loading images (IntersectionObserver)
 * - Image carousel navigation
 * - Status ribbons (Sold, Reserved)
 * - Dual currency support (USD/XCG)
 * - Mobile-optimized touch interactions
 * - Smart back navigation via useNavigate state
 * - Grid/List view variants
 */
const ListingCard = memo(({ 
  listing, 
  index = 0, 
  compact = false,
  variant = 'grid' // 'grid' or 'list'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentIsland } = useIsland();
  const { currency, formatDualPrice } = useCurrency();
  const { t, language } = useLanguage();
  
  // Computed values - always called (hooks rules)
  const isPromoted = listing.is_boosted;
  const isSold = listing.status === 'sold';
  const isReserved = listing.status === 'reserved';
  const isAuction = listing.listing_type === 'auction';
  const hasUSDT = listing.accepts_usdt;
  
  // Check if auction has ended
  const auctionEndTime = listing.auction?.auction_end_time || listing.auction?.end_time;
  const isAuctionEnded = isAuction && auctionEndTime && new Date(auctionEndTime) <= new Date();
  
  // Check if yard sale has ended
  const yardSaleEndDate = listing.yard_sale?.end_date;
  const isYardSaleEnded = listing.listing_type === 'yard_sale' && yardSaleEndDate && new Date(yardSaleEndDate) <= new Date();
  
  // Get island from listing location for badge
  const listingIsland = useMemo(() => {
    const loc = typeof listing.location === 'object' ? listing.location : {};
    return loc.island || loc.country || loc.area || '';
  }, [listing.location]);
  
  // Get primary image
  const primaryImage = listing.images?.[0] || '/placeholder.jpg';
  
  // Format price using dual currency formatter (same as ListingCardPrice)
  const formattedPrice = useMemo(() => {
    const pricing = listing.pricing || { amount: listing.price || 0, currency: 'USD' };
    
    if (!pricing.amount) {
      return t('price_on_request_short') || 'Price TBD';
    }
    
    // Handle auction pricing
    if (listing.listing_type === 'auction') {
      const amount = listing.auction?.current_bid || listing.auction?.starting_price || pricing.amount;
      const prices = formatDualPrice(amount, 'USD');
      return currency === 'XCG' ? prices.xcg : prices.usd;
    }
    
    // Handle yard sale pricing
    if (listing.listing_type === 'yard_sale') {
      if (listing.yard_sale?.bundle_price) {
        const bundlePrices = formatDualPrice(parseFloat(listing.yard_sale.bundle_price), 'USD');
        return currency === 'XCG' ? bundlePrices.xcg : bundlePrices.usd;
      }
      if (pricing.amount > 0) {
        const prices = formatDualPrice(pricing.amount, pricing.currency);
        return currency === 'XCG' ? prices.xcg : prices.usd;
      }
      return t('see_items') || 'See items';
    }
    
    // Regular pricing
    const prices = formatDualPrice(pricing.amount, pricing.currency);
    let result = currency === 'XCG' ? prices.xcg : prices.usd;
    
    // Add rental period if applicable
    if (listing.listing_type === 'rent') {
      const rentalPeriod = pricing.rental_period || (pricing.is_hourly ? 'hourly' : 'monthly');
      result += ` / ${getRentalPeriodLabel(rentalPeriod, language)}`;
    }
    
    return result;
  }, [listing, currency, formatDualPrice, t, language]);
  
  // Check if price was reduced and format original price
  const priceReductionInfo = useMemo(() => {
    const pricing = listing.pricing || {};
    if (!pricing.original_price || pricing.original_price <= pricing.amount) {
      return null;
    }
    
    // Don't show for auctions or yard sales
    if (listing.listing_type === 'auction' || listing.listing_type === 'yard_sale') {
      return null;
    }
    
    const originalPrices = formatDualPrice(pricing.original_price, pricing.currency);
    return {
      originalPrice: currency === 'XCG' ? originalPrices.xcg : originalPrices.usd,
      isReduced: true
    };
  }, [listing, currency, formatDualPrice]);
  
  // Navigate to listing with return path state
  const handleNavigateToListing = (e) => {
    e.preventDefault();
    const returnState = {
      returnPath: location.pathname + location.search,
      scrollY: window.scrollY,
      fromMarketplace: location.pathname.includes('/marketplace') || location.pathname.includes('/categories')
    };
    const listingPath = listing.slug || listing.id;
    navigate(`/listing/${listingPath}`, { state: returnState });
  };
  
  // List card classes
  const listCardClasses = useMemo(() => {
    let classes = 'flex gap-3 p-2 rounded-xl border transition-all bg-card hover:shadow-md cursor-pointer';
    if (isSold) {
      classes += ' border-red-500/30 ring-1 ring-red-500/30 bg-red-500/5';
    } else if (isReserved) {
      classes += ' border-orange-500/30 ring-1 ring-orange-500/30 bg-orange-500/5';
    } else if (isPromoted) {
      classes += ' border-primary/30 ring-1 ring-primary/30';
    } else {
      classes += ' border-border hover:border-primary/50';
    }
    return classes;
  }, [isSold, isReserved, isPromoted]);
  
  // Grid card classes
  const gridCardClasses = useMemo(() => {
    const baseClasses = `listing-card ${compact ? 'rounded-xl' : 'rounded-2xl'} overflow-hidden flex flex-col border transition-all h-full`;
    
    if (isSold) {
      return `${baseClasses} border-red-500/30 bg-card ring-2 ring-red-500/50 shadow-[0_0_20px_rgba(239,68,68,0.4)] bg-red-500/5`;
    }
    if (isReserved) {
      return `${baseClasses} border-orange-500/30 bg-card ring-2 ring-orange-500/50 shadow-[0_0_20px_rgba(249,115,22,0.4)] bg-orange-500/5`;
    }
    if (isPromoted) {
      return `${baseClasses} border-border bg-card ring-1 ring-slate-400/40 dark:ring-slate-500/40 hover:ring-slate-400/60 dark:hover:ring-slate-500/60 hover:shadow-md`;
    }
    return `${baseClasses} border-border bg-card hover:border-primary/50 hover:shadow-md`;
  }, [isSold, isReserved, isPromoted, compact]);
  
  // Content background based on status
  const contentClasses = useMemo(() => {
    const base = `flex flex-col flex-1 ${compact ? 'p-2 sm:p-3' : 'p-3 sm:p-4'}`;
    if (isSold) {
      return `${base} bg-gradient-to-b from-red-500/20 to-red-500/10`;
    }
    if (isReserved) {
      return `${base} bg-gradient-to-b from-orange-500/20 to-orange-500/10`;
    }
    return base;
  }, [isSold, isReserved, compact]);
  
  // List view layout (Premium Refactor)
  if (variant === 'list') {
    // Check for under offer status
    const isUnderOffer = listing.status === 'under_offer';
    
    return (
      <div
        onClick={handleNavigateToListing}
        className={`${listCardClasses} flex gap-4 p-3 rounded-xl border bg-card transition-all duration-200 hover:shadow-sm hover:border-primary/40 cursor-pointer group`}
        data-testid={`listing-card-${listing.id}`}
      >
        {/* Thumbnail Container */}
        <div className={`relative w-28 h-24 sm:w-32 sm:h-24 flex-shrink-0 rounded-lg overflow-hidden bg-secondary/30 ${isPromoted ? 'ring-2 ring-primary/40' : ''}`}>
          <img 
            src={primaryImage} 
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
          {/* Status ribbons - horizontal banners matching Grid View */}
          {isSold && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="absolute inset-0 bg-black/15" />
              <div className="absolute left-0 right-0 bg-red-600/85 backdrop-blur-[2px] py-1.5">
                <span className="block text-white text-center font-bold text-[10px] uppercase tracking-wider">
                  {t('status_sold') || 'Sold'}
                </span>
              </div>
            </div>
          )}
          {isReserved && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="absolute inset-0 bg-black/15" />
              <div className="absolute left-0 right-0 bg-orange-500/85 backdrop-blur-[2px] py-1.5">
                <span className="block text-white text-center font-bold text-[10px] uppercase tracking-wider">
                  {t('status_reserved') || 'Reserved'}
                </span>
              </div>
            </div>
          )}
          {isUnderOffer && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="absolute inset-0 bg-black/15" />
              <div className="absolute left-0 right-0 bg-amber-500/85 backdrop-blur-[2px] py-1.5">
                <span className="block text-white text-center font-bold text-[10px] uppercase tracking-wider">
                  {t('status_under_offer') || 'Under Offer'}
                </span>
              </div>
            </div>
          )}
          {isPromoted && !isSold && !isReserved && !isUnderOffer && (
            <div className="absolute top-1.5 left-1.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-800 dark:bg-slate-700 text-white text-[10px] font-medium rounded-full shadow-sm">
                <Zap className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
                PROMOTED
              </span>
            </div>
          )}
        </div>
        
        {/* Content Container */}
        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
          <div className="space-y-1">
            {/* Top row: Type Badge */}
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-semibold tracking-wider uppercase ${
                (isAuction && isAuctionEnded) || (listing.listing_type === 'yard_sale' && isYardSaleEnded) ? 'text-muted-foreground' :
                isAuction ? 'text-amber-500' : 
                listing.listing_type === 'rent' || listing.listing_type === 'rental' ? 'text-purple-500' : 
                listing.listing_type === 'yard_sale' ? 'text-emerald-500' :
                'text-primary'
              }`}>
                {isAuction ? (
                  isAuctionEnded 
                    ? `${t('listing_type_auction') || 'Auction'} • ${t('ended') || 'Ended'}`
                    : (t('listing_type_auction') || 'Auction')
                ) : 
                 listing.listing_type === 'rent' || listing.listing_type === 'rental' ? (t('listing_type_rental') || 'Rental') : 
                 listing.listing_type === 'yard_sale' ? (
                   isYardSaleEnded
                     ? `${t('listing_type_yard_sale') || 'Yard Sale'} • ${t('ended') || 'Ended'}`
                     : (t('listing_type_yard_sale') || 'Yard Sale')
                 ) :
                 (t('listing_type_sale') || 'For Sale')}
              </span>
            </div>

            {/* Title */}
            <h3 className="text-sm font-medium text-foreground tracking-tight line-clamp-2 group-hover:text-primary transition-colors pr-4">
              {listing.title}
            </h3>
          </div>
          
          {/* Bottom section: Price row + Metadata row */}
          <div className="mt-auto pt-1.5 space-y-1">
            {/* Price row with badges */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Price - stacked layout when price is reduced */}
              {priceReductionInfo ? (
                <div className="flex flex-col">
                  <span className="text-[10px] text-muted-foreground line-through leading-tight">
                    {priceReductionInfo.originalPrice}
                  </span>
                  <span className="text-sm font-bold tracking-tight text-green-600 dark:text-green-500 leading-tight">
                    {formattedPrice}
                  </span>
                </div>
              ) : (
                <span className={`text-sm font-bold tracking-tight ${isAuction ? 'text-amber-500' : 'text-foreground'}`}>
                  {formattedPrice}
                </span>
              )}

              {/* USDT badge */}
              {hasUSDT && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-teal-500/10 border border-teal-500/20 text-teal-600 dark:text-teal-400 text-[9px] font-medium rounded">
                  <Wallet className="w-2.5 h-2.5" />
                  USDT
                </span>
              )}
              
              {/* Auction Bids badge */}
              {isAuction && listing.bid_count > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-[9px] font-medium rounded">
                  <Gavel className="w-2.5 h-2.5" />
                  {listing.bid_count} {listing.bid_count === 1 ? (t('bid') || 'bid') : (t('bids') || 'bids')}
                </span>
              )}
              
              {/* Yard Sale Items badge */}
              {listing.listing_type === 'yard_sale' && listing.items_count > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-[9px] font-medium rounded">
                  <Package className="w-2.5 h-2.5" />
                  {listing.items_count} {listing.items_count === 1 ? (t('item') || 'item') : (t('items') || 'items')}
                </span>
              )}
            </div>
            
            {/* Location + Views metadata */}
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-medium">
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-muted-foreground/70" />
                <span className="truncate max-w-[100px]">{listingIsland || (t('unknown_location') || 'Unknown')}</span>
              </span>
              {listing.views > 0 && (
                <span className="flex items-center gap-1 border-l border-border pl-2">
                  <Eye className="w-3 h-3 text-muted-foreground/70" />
                  {listing.views}
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Right side Actions (Favorite) */}
        <div className="flex-shrink-0 flex flex-col justify-start pt-0.5">
          <FavoriteButton listingId={listing.id} size="tiny" />
        </div>
      </div>
    );
  }
  
  // Grid view layout (default)
  return (
    <div
      className={gridCardClasses}
      data-testid={`listing-card-${listing.id}`}
    >
      {/* Image Section */}
      <ListingCardImage
        listingId={listing.id}
        images={listing.images}
        title={listing.title}
        category={listing.category}
        compact={compact}
        listingType={listing.listing_type}
      >
        {/* Badges and overlays rendered as children */}
        <ListingCardBadges listing={listing} />
        
        {/* Top right actions (featured, favorite) */}
        <div className="absolute top-2 right-2 flex items-center gap-1 z-10">
          {/* Featured icon - always visible when promoted */}
          <FeaturedIcon listing={listing} />
          
          {/* Favorite button */}
          <FavoriteButton listingId={listing.id} size={compact ? "tiny" : "small"} />
        </div>
      </ListingCardImage>
      
      {/* Content Section - using onClick for smart navigation with state */}
      <div 
        onClick={handleNavigateToListing}
        className={`${contentClasses} cursor-pointer`}
      >
        {/* Header (category, title) */}
        <ListingCardHeader listing={listing} compact={compact} />
        
        {/* Price */}
        <ListingCardPrice listing={listing} compact={compact} />
        
        {/* Footer (location, views, seller) */}
        <ListingCardFooter listing={listing} compact={compact} currentIsland={currentIsland} />
      </div>
    </div>
  );
});

ListingCard.displayName = 'ListingCard';

export default ListingCard;
